import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useBreadcrumbs,
  useCreateFolder,
  useFiles,
  useFolders,
  useMoveFile,
  useMoveFolder,
  useSearch,
} from '../../lib/queries.ts';
import { useFileUpload } from './useFileUpload.ts';
import { filesFromDrop } from './dropped-entries.ts';
import { useQueryClient } from '@tanstack/react-query';
import { duplicateInPlace, useDriveClipboard } from './DriveClipboard.tsx';
import { refreshFolder } from './refreshFolder.ts';
import { useToast } from '../../components/Toast.tsx';
import { Alert, Button, EmptyState, Icons, Spinner, cx } from '../../components/ui.tsx';
import { Tooltip } from '../../components/Tooltip.tsx';
import { Breadcrumbs } from './Breadcrumbs.tsx';
import { ItemColumns, ItemRow } from './ItemRow.tsx';
import { SelectBox, SelectionBar } from './SelectionBar.tsx';
import { isDriveDrag, type DragPayload } from './dragging.ts';
import { useMoveSpreadsheet } from '../../lib/sheet-queries.ts';
import { useMoveDocument } from '../../lib/document-queries.ts';
import { useSelection } from './useSelection.ts';
import { ApiError } from '../../lib/api.ts';
import { useSpreadsheets } from '../../lib/sheet-queries.ts';
import { NewSheetButton } from '../sheets/NewSheetButton.tsx';
import { NewMenu } from './NewMenu.tsx';
import { useAccess } from '../../lib/queries.ts';
import { SheetRow } from '../sheets/SheetRow.tsx';
import { DocumentRow } from '../documents/DocumentRow.tsx';
import { useDocuments } from '../../lib/document-queries.ts';
import type { DriveSearchResults } from '../../lib/queries.ts';
import { useDialog } from '../../components/Dialog.tsx';
import { useAuth } from '../auth/AuthContext.tsx';
import { can } from '@aq/shared';
import { SavePicker } from '../sheets/SavePicker.tsx';

const EMPTY_SEARCH: DriveSearchResults = {
  folders: [],
  files: [],
  spreadsheets: [],
  documents: [],
};

/** Where the rows-or-tiles choice is remembered. */
const VIEW_KEY = 'aq.drive.view';

export function DrivePage() {
  // Folder lives in the URL, so back/forward and bookmarks work the way the
  // user expects rather than being trapped in component state.
  const [params, setParams] = useSearchParams();
  const folderId = params.get('folder');

  const [search, setSearch] = useState('');
  const [dragging, setDragging] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);

  /**
   * Rows or tiles, remembered.
   *
   * A person who picks one has picked it: Explorer, Finder and Drive all keep
   * the choice, and a view that resets on every visit is a view you have to
   * choose again every visit. Kept per browser rather than per account — it is
   * a preference about this screen, not a fact about the work.
   */
  const [view, setView] = useState<'list' | 'grid'>(() =>
    localStorage.getItem(VIEW_KEY) === 'grid' ? 'grid' : 'list',
  );
  useEffect(() => {
    localStorage.setItem(VIEW_KEY, view);
  }, [view]);
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);

  const folders = useFolders(folderId);
  const files = useFiles(folderId);
  const spreadsheets = useSpreadsheets(folderId);
  const documents = useDocuments(folderId);
  const crumbs = useBreadcrumbs(folderId);
  const searchResults = useSearch(search);
  const createFolder = useCreateFolder(folderId);
  const moveFile = useMoveFile(folderId);
  const moveFolder = useMoveFolder(folderId);
  const moveSpreadsheet = useMoveSpreadsheet();
  const moveDocument = useMoveDocument();
  const clipboard = useDriveClipboard();
  const client = useQueryClient();
  const toast = useToast();

  const selection = useSelection();

  const navigate = useCallback(
    (id: string | null) => {
      setParams(id ? { folder: id } : {});
      setSearch('');
      // A selection that survives navigation is one nobody can see, and
      // "delete selected" would then act on rows that are off screen.
      selection.clear();
    },
    [setParams, selection],
  );

  /**
   * Send chosen files to the upload queue.
   *
   * Name clashes are settled first, by the shared hook — the New sheet menu's
   * import does exactly the same thing and deserves the same answers.
   */
  const dialog = useDialog();
  const { user } = useAuth();
  /**
   * Whether anything may be put here — the role and the folder, together.
   *
   * The role alone used to answer this, which was right while every account
   * could see every folder. Now that the Drive is closed it is two questions:
   * Staff hold file:write everywhere and still cannot write at the root or in
   * a folder nobody gave them.
   *
   * The folder half comes from the server. A copy of the rule here would be a
   * second opinion about access, and the one that drifts is the one that draws
   * a button the server then refuses.
   *
   * Undecided while the answer is in flight, so the buttons arrive rather than
   * appearing and then vanishing — which reads as a glitch, and on a
   * restricted account reads as something being taken away.
   */
  const access = useAccess(folderId);
  const mayWriteHere = access.data
    ? access.data.level === 'EDIT' || access.data.level === 'MANAGE'
    : false;
  const mayWrite = Boolean(user && can(user.role, 'file:write')) && mayWriteHere;
  const startUpload = useFileUpload(folderId);
  const handleFiles = useCallback(
    async (list: FileList | readonly File[] | null) => {
      // Dropping files is an upload like any other, and a read-only account
      // has nowhere to put them. Silence beats a row of refusals.
      if (!mayWrite) return;
      setError(null);
      await startUpload(list);
    },
    [mayWrite, startUpload],
  );

  /**
   * Ask where before sending anything.
   *
   * Picking files off the machine says what to upload and nothing about
   * where it should land, and the answer used to be "wherever you happened to
   * be standing" — which for anyone starting from the top of the Drive meant
   * the root, every time, and a tidy-up afterwards. Every drive worth the
   * name asks, so this asks, with the folder currently open already chosen so
   * that answering is one keypress for the common case.
   *
   * Only the two Upload controls go through here. A drop already names its
   * destination by where it was dropped, and re-asking would contradict it.
   */
  const uploadWithDestination = useCallback(
    (list: FileList | readonly File[] | null, what: 'files' | 'folder') => {
      const chosen = list ? Array.from(list) : [];
      if (chosen.length === 0) return;
      setError(null);

      dialog.open((close) => (
        <SavePicker
          title={
            what === 'folder'
              ? 'Upload folder'
              : chosen.length === 1
                ? `Upload "${chosen[0]!.name}"`
                : `Upload ${chosen.length} files`
          }
          subtitle="Choose where they are put."
          askForName={false}
          initialName=""
          initialFolderId={folderId}
          confirmLabel="Upload here"
          onConfirm={(target) => {
            void startUpload(chosen, target.folderId);
          }}
          onClose={close}
        />
      ));
    },
    [dialog, folderId, startUpload],
  );

  // Drag events fire for every child element, so a plain boolean flickers.
  // Counting enter/leave pairs is what keeps the highlight stable.
  //
  // A row being dragged onto a folder is not an upload, so none of this should
  // react to it — without the guard, dragging a file two rows down lit up
  // "Drop files to upload" across the whole page.
  function onDragEnter(event: DragEvent) {
    if (isDriveDrag(event)) return;
    event.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  }

  function onDragLeave(event: DragEvent) {
    if (isDriveDrag(event)) return;
    event.preventDefault();
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) setDragging(false);
  }

  function onDrop(event: DragEvent) {
    if (isDriveDrag(event)) return;
    event.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    // The entries must come out of the DataTransfer before this handler
    // returns, so the reading starts here rather than inside handleFiles.
    void filesFromDrop(event.dataTransfer).then(handleFiles);
  }

  /** Paste into the folder currently on screen. */
  const pasteHere = useCallback(async () => {
    try {
      const done = await clipboard.paste(folderId);
      if (!done) return;
      await refreshFolder(client, folderId);
      toast.success(
        `${done.count === 1 ? 'Item' : `${done.count} items`} ${done.mode === 'cut' ? 'moved' : 'copied'} here`,
      );
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not paste that.');
    }
  }, [clipboard, folderId, client, toast]);

  /**
   * Explorer's keys, on the listing.
   *
   * Ignored while a field has focus: Ctrl+C in a rename box has to copy the
   * text being typed, not the row behind it.
   */
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (!(event.ctrlKey || event.metaKey)) return;

      const target = event.target;
      if (
        target instanceof HTMLElement &&
        target.closest('input, textarea, [contenteditable="true"]')
      ) {
        return;
      }

      const key = event.key.toLowerCase();
      if (key === 'v') {
        if (!clipboard.clipboard) return;
        event.preventDefault();
        void pasteHere();
        return;
      }

      if (selection.count === 0) return;

      if (key === 'c') {
        event.preventDefault();
        clipboard.copy(selection.payload);
      } else if (key === 'x') {
        event.preventDefault();
        clipboard.cut(selection.payload);
      } else if (key === 'd') {
        event.preventDefault();
        void (async () => {
          try {
            await duplicateInPlace(selection.payload, folderId);
            await refreshFolder(client, folderId);
            toast.success(`${selection.count === 1 ? 'Item' : 'Items'} duplicated`);
          } catch (caught) {
            setError(caught instanceof ApiError ? caught.message : 'Could not duplicate that.');
          }
        })();
      }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [clipboard, selection, folderId, client, toast, pasteHere]);

  /**
   * Move a dragged row into an ancestor folder.
   *
   * The counterpart to dropping onto a folder row: this is the only way back
   * up, since nothing in the listing represents the folder above.
   */
  async function moveInto(target: string | null, payload: DragPayload) {
    if (payload.kind === 'folder' && payload.id === target) return;
    try {
      if (payload.kind === 'file') await moveFile.mutateAsync({ id: payload.id, targetId: target });
      else if (payload.kind === 'folder')
        await moveFolder.mutateAsync({ id: payload.id, targetId: target });
      else if (payload.kind === 'spreadsheet')
        await moveSpreadsheet.mutateAsync({ id: payload.id, folderId: target });
      else await moveDocument.mutateAsync({ id: payload.id, folderId: target });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not move that.');
    }
  }

  async function submitFolder() {
    const name = newName.trim();
    if (!name) return;
    setError(null);
    try {
      await createFolder.mutateAsync(name);
      setNewName('');
      setCreating(false);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not create the folder.');
    }
  }

  const searching = search.trim().length >= 2;
  const folderList = folders.data ?? [];
  const fileList = files.data?.items ?? [];
  const sheetList = spreadsheets.data ?? [];
  const documentList = documents.data ?? [];

  /** Everything on screen, in the shape the selection helper wants. */
  const everything = [
    ...folderList.map((folder) => ({ kind: 'folder' as const, id: folder.id })),
    ...sheetList.map((sheet) => ({ kind: 'spreadsheet' as const, id: sheet.id })),
    ...documentList.map((document) => ({ kind: 'document' as const, id: document.id })),
    ...fileList.map((file) => ({ kind: 'file' as const, id: file.id })),
  ];
  const isEmpty =
    !searching &&
    folderList.length === 0 &&
    fileList.length === 0 &&
    sheetList.length === 0 &&
    documentList.length === 0;
  const loading =
    folders.isLoading || files.isLoading || spreadsheets.isLoading || documents.isLoading;

  return (
    <div
      onDragEnter={onDragEnter}
      onDragOver={(event) => {
        if (isDriveDrag(event)) return;
        event.preventDefault();
      }}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className="relative flex h-full flex-col"
    >
      {dragging ? (
        <div className="pointer-events-none absolute inset-4 z-20 flex items-center justify-center rounded-xl border-2 border-dashed border-blue-600 bg-blue-50/90">
          <p className="text-h3 text-blue-700">Drop files to upload</p>
        </div>
      ) : null}

      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
        <Breadcrumbs
          items={crumbs.data ?? []}
          onNavigate={navigate}
          onDropInto={(target, payload) => void moveInto(target, payload)}
        />

        <div className="flex flex-wrap items-center gap-2">
          <label className="relative">
            <span className="sr-only">Search files</span>
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted">
              <Icons.Search />
            </span>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search files"
              className="h-control w-56 rounded-md border border-border-input bg-surface pl-9 pr-3 text-body-sm text-ink"
            />
          </label>

          <ViewToggle view={view} onView={setView} />

          {/* Only once there is something to paste — a permanently disabled
              button is furniture — and only where it could land. Pasting puts
              something here like any upload does, so it answers to the same
              question. */}
          {clipboard.clipboard && mayWrite ? (
            <Tooltip
              label={clipboard.clipboard.mode === 'cut' ? 'Move here' : 'Paste a copy here'}
              shortcut="Ctrl+V"
            >
              <Button
                icon={<Icons.Clipboard />}
                onClick={() => void pasteHere()}
                disabled={clipboard.pasting}
              >
                {clipboard.pasting
                  ? 'Pasting…'
                  : `Paste ${clipboard.clipboard.count > 1 ? `(${clipboard.clipboard.count})` : ''}`}
              </Button>
            </Tooltip>
          ) : null}

          {/*
            Everything behind New puts something into the system, so a Viewer
            is not shown it. The server refuses each of these anyway — they are
            all file:write — but a menu of things that answer "no" is not a
            read-only account, it is a broken one.
          */}
          {mayWrite ? (
            <NewMenu
              folderId={folderId}
              onError={setError}
              onNewFolder={() => setCreating(true)}
              onUploadFiles={() => fileInput.current?.click()}
              onUploadFolder={() => folderInput.current?.click()}
            />
          ) : null}

          <div className="hidden">
            <Button
              variant="primary"
              icon={<Icons.Upload />}
              onClick={() => fileInput.current?.click()}
            >
              Upload
            </Button>
            <Tooltip
              label="Upload a folder"
              detail="Sends the folder and everything inside it, keeping its structure."
            >
              {/*
                Labelled, not just an icon.

                As an unlabelled folder glyph beside a large "Upload" button
                this read as decoration, and the person who wanted to send a
                folder pressed Upload instead — which opens a *file* dialog,
                where a folder can only be opened, not chosen. They concluded
                the Drive could not take folders at all.

                The label is hidden below `sm`, where the row has to stay on
                one line; the tooltip and aria-label carry it there.
              */}
              <button
                type="button"
                aria-label="Upload a folder"
                onClick={() => folderInput.current?.click()}
                className="ml-1 flex h-control items-center gap-1.5 rounded-md border border-border-input px-2 text-body-sm text-body transition-fast hover:bg-sunken"
              >
                <Icons.Folder />
                <span className="hidden sm:inline">Folder</span>
              </button>
            </Tooltip>
          </div>

          <input
            ref={fileInput}
            type="file"
            multiple
            className="hidden"
            onChange={(event) => {
              uploadWithDestination(event.target.files, 'files');
              event.target.value = '';
            }}
          />
          {/*
            The same control with `webkitdirectory`, which is the only way a
            browser will hand over a folder — picking one in the ordinary
            dialog just opens it. Non-standard but universally supported, and
            it is what every drive does. React needs it lowercased in JSX.
          */}
          <input
            ref={folderInput}
            type="file"
            multiple
            {...({ webkitdirectory: '' } as Record<string, string>)}
            className="hidden"
            onChange={(event) => {
              uploadWithDestination(event.target.files, 'folder');
              event.target.value = '';
            }}
          />
        </div>
      </header>

      {selection.count > 0 ? (
        <div className="mt-4">
          <SelectionBar
            count={selection.count}
            payload={selection.payload}
            folderId={folderId}
            onClear={selection.clear}
            onChanged={() => {
              void folders.refetch();
              void files.refetch();
              void spreadsheets.refetch();
              void documents.refetch();
            }}
          />
        </div>
      ) : null}

      <div className="flex-1 overflow-y-auto py-4">
        {error ? (
          <div className="mb-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {/* Upload progress is reported by the bar at the top of the window,
            not here — the whole point is that you can leave this page. */}

        {creating ? (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-border-strong bg-surface p-3">
            <span className="text-blue-600">
              <Icons.Folder />
            </span>
            <input
              autoFocus
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void submitFolder();
                if (event.key === 'Escape') {
                  setCreating(false);
                  setNewName('');
                }
              }}
              placeholder="Folder name"
              className="h-9 flex-1 rounded-md border border-border-input px-3 text-body-sm text-ink"
            />
            <Button variant="primary" onClick={() => void submitFolder()}>
              Create
            </Button>
            <Button
              onClick={() => {
                setCreating(false);
                setNewName('');
              }}
            >
              Cancel
            </Button>
          </div>
        ) : null}

        {loading ? (
          <div className="flex items-center gap-2 px-1 py-8 text-body-sm text-muted">
            <Spinner /> Loading…
          </div>
        ) : searching ? (
          <SearchResults
            results={searchResults.data ?? EMPTY_SEARCH}
            loading={searchResults.isLoading}
            folderId={folderId}
            selection={selection}
            onOpenFolder={navigate}
          />
        ) : isEmpty ? (
          <EmptyState
            title="This folder is empty"
            description="Start a new sheet, drag files here to upload them, or create a folder to organise your documents."
            action={<NewSheetButton folderId={folderId} onError={setError} />}
          />
        ) : view === 'grid' ? (
          /*
           * The same items, as tiles.
           *
           * One list of components either way — each row knows how to draw
           * itself as a tile — so opening, renaming, dragging into a folder
           * and every entry in the context menu behave identically in both
           * views rather than being written twice.
           */
          <ul className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {folderList.map((folder) => (
              <ItemRow
                key={folder.id}
                view="grid"
                kind="folder"
                folder={folder}
                parentId={folderId}
                onOpen={() => navigate(folder.id)}
                selected={selection.has('folder', folder.id)}
                onToggleSelected={() => selection.toggle('folder', folder.id)}
                selectionActive={selection.count > 0}
              />
            ))}
            {sheetList.map((sheet) => (
              <SheetRow
                key={sheet.id}
                view="grid"
                sheet={sheet}
                selected={selection.has('spreadsheet', sheet.id)}
                onToggleSelected={() => selection.toggle('spreadsheet', sheet.id)}
                selectionActive={selection.count > 0}
              />
            ))}
            {documentList.map((document) => (
              <DocumentRow
                key={document.id}
                view="grid"
                document={document}
                selected={selection.has('document', document.id)}
                onToggleSelected={() => selection.toggle('document', document.id)}
                selectionActive={selection.count > 0}
              />
            ))}
            {fileList.map((file) => (
              <ItemRow
                key={file.id}
                view="grid"
                kind="file"
                file={file}
                parentId={folderId}
                selected={selection.has('file', file.id)}
                onToggleSelected={() => selection.toggle('file', file.id)}
                selectionActive={selection.count > 0}
              />
            ))}
          </ul>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border-strong bg-surface">
            <table className="w-full table-fixed">
              <ItemColumns />
              <thead>
                <tr className="border-b border-border-strong bg-sunken">
                  <th className="pl-4">
                    <SelectBox
                      checked={selection.count > 0 && selection.count === everything.length}
                      indeterminate={selection.count > 0}
                      label="Select everything in this folder"
                      onChange={() =>
                        selection.count === everything.length
                          ? selection.clear()
                          : selection.selectAll(everything)
                      }
                    />
                  </th>
                  <th className="px-4 py-2.5 text-left text-micro font-semibold uppercase tracking-wide text-muted">
                    Name
                  </th>
                  <th className="px-4 py-2.5 text-right text-micro font-semibold uppercase tracking-wide text-muted max-md:hidden">
                    Size
                  </th>
                  <th className="px-4 py-2.5 text-right text-micro font-semibold uppercase tracking-wide text-muted max-md:hidden">
                    Modified
                  </th>
                  <th className="px-4 py-2.5 text-right text-micro font-semibold uppercase tracking-wide text-muted">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {folderList.map((folder) => (
                  <ItemRow
                    key={folder.id}
                    kind="folder"
                    folder={folder}
                    parentId={folderId}
                    onOpen={() => navigate(folder.id)}
                    selected={selection.has('folder', folder.id)}
                    onToggleSelected={() => selection.toggle('folder', folder.id)}
                    selectionActive={selection.count > 0}
                  />
                ))}
                {/* Sheets before uploads: they are the documents he works
                    in, and uploads are the supporting paperwork. */}
                {sheetList.map((sheet) => (
                  <SheetRow
                    key={sheet.id}
                    sheet={sheet}
                    selected={selection.has('spreadsheet', sheet.id)}
                    onToggleSelected={() => selection.toggle('spreadsheet', sheet.id)}
                    selectionActive={selection.count > 0}
                  />
                ))}

                {/* Documents after sheets and before uploads, on the same
                    reasoning: the things worked in come before the things
                    filed. */}
                {documentList.map((document) => (
                  <DocumentRow
                    key={document.id}
                    document={document}
                    selected={selection.has('document', document.id)}
                    onToggleSelected={() => selection.toggle('document', document.id)}
                    selectionActive={selection.count > 0}
                  />
                ))}
                {fileList.map((file) => (
                  <ItemRow
                    key={file.id}
                    kind="file"
                    file={file}
                    parentId={folderId}
                    selected={selection.has('file', file.id)}
                    onToggleSelected={() => selection.toggle('file', file.id)}
                    selectionActive={selection.count > 0}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SearchResults({
  results,
  loading,
  folderId,
  selection,
  onOpenFolder,
}: {
  results: DriveSearchResults;
  loading: boolean;
  folderId: string | null;
  selection: ReturnType<typeof useSelection>;
  onOpenFolder: (id: string) => void;
}) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 px-1 py-8 text-body-sm text-muted">
        <Spinner /> Searching…
      </div>
    );
  }

  const total =
    results.folders.length +
    results.files.length +
    results.spreadsheets.length +
    results.documents.length;

  if (total === 0) {
    return (
      <EmptyState
        title="No matches"
        description="Nothing in your drive matches that — folders, sheets, documents and files were all searched."
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border-strong bg-surface">
      <table className="w-full table-fixed">
        <ItemColumns />
        <tbody>
          {/* Folders first, then the things people actually work in, then
              uploads — the same order the folder listing uses, so a search
              result looks like the place it came from. */}
          {results.folders.map((folder) => (
            <ItemRow
              key={folder.id}
              kind="folder"
              folder={folder}
              parentId={folderId}
              selected={selection.has('folder', folder.id)}
              onToggleSelected={() => selection.toggle('folder', folder.id)}
              selectionActive={selection.count > 0}
              onOpen={() => onOpenFolder(folder.id)}
            />
          ))}

          {results.spreadsheets.map((sheet) => (
            <SheetRow
              key={sheet.id}
              sheet={sheet}
              selected={selection.has('spreadsheet', sheet.id)}
              onToggleSelected={() => selection.toggle('spreadsheet', sheet.id)}
              selectionActive={selection.count > 0}
            />
          ))}

          {results.documents.map((doc) => (
            <DocumentRow
              key={doc.id}
              document={doc}
              selected={selection.has('document', doc.id)}
              onToggleSelected={() => selection.toggle('document', doc.id)}
              selectionActive={selection.count > 0}
            />
          ))}

          {results.files.map((file) => (
            <ItemRow
              key={file.id}
              kind="file"
              file={file}
              parentId={folderId}
              selected={selection.has('file', file.id)}
              onToggleSelected={() => selection.toggle('file', file.id)}
              selectionActive={selection.count > 0}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Rows or tiles.
 *
 * Two states, so two buttons rather than a menu: the choice is visible, and
 * making it costs one click instead of two. Drive, Explorer and Finder all put
 * it in the same corner, which is the reason to put it there too — this is a
 * control people arrive already knowing.
 */
function ViewToggle({
  view,
  onView,
}: {
  view: 'list' | 'grid';
  onView: (next: 'list' | 'grid') => void;
}) {
  return (
    <div className="flex items-center rounded-md border border-border-strong bg-surface p-0.5">
      {[
        { id: 'list' as const, label: 'List view', glyph: <ListGlyph /> },
        { id: 'grid' as const, label: 'Grid view', glyph: <TileGlyph /> },
      ].map((option) => (
        <Tooltip key={option.id} label={option.label}>
          <button
            type="button"
            aria-label={option.label}
            aria-pressed={view === option.id}
            onClick={() => onView(option.id)}
            className={cx(
              'flex h-7 w-8 items-center justify-center rounded transition-fast',
              view === option.id
                ? 'bg-[var(--blue-100)] text-[var(--blue-800)]'
                : 'text-muted hover:bg-sunken hover:text-ink',
            )}
          >
            {option.glyph}
          </button>
        </Tooltip>
      ))}
    </div>
  );
}

/** Three rules: a list. */
function ListGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M2.5 4h11M2.5 8h11M2.5 12h11" />
    </svg>
  );
}

/** Four squares: a grid. */
function TileGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden
    >
      <rect x="2.5" y="2.5" width="4.5" height="4.5" rx="1" />
      <rect x="9" y="2.5" width="4.5" height="4.5" rx="1" />
      <rect x="2.5" y="9" width="4.5" height="4.5" rx="1" />
      <rect x="9" y="9" width="4.5" height="4.5" rx="1" />
    </svg>
  );
}
